import { useQueryClient } from 'react-query';

import { useAppSocket, useIsSuccessful } from '@/common/hooks';
import { BaseQueryParams } from '@/common/interfaces';

import { DomainsQueryKeys, useGetConfigDomainsQuery } from '@/modules/Domains';
import { ProxyServerAddressesGatewayKeys, ProxyServerConfigurationStatuses, ProxyServerHealthStatuses } from '@/modules/ProxyServers';

type Data =
  | {
      entity: 'config';
      addressId: string;
      proxyServerId: string;
      status: ProxyServerConfigurationStatuses;
    }
  | {
      entity: 'health';
      addressId: string;
      proxyServerId: string;
      status: ProxyServerHealthStatuses;
    };

interface Args {
  domainId: string;
}

export const useUpdateProxyServerStatuses = ({ domainId }: Args) => {
  const queryClient = useQueryClient();

  const isDomainDeploymentInfoSuccess = useIsSuccessful({ queryKey: [DomainsQueryKeys.GetConfig, domainId] });

  useAppSocket<Omit<Data, 'entity'>>(ProxyServerAddressesGatewayKeys.UpdateConfigurationStatus, ({ lastMessage }) => {
    const { addressId, proxyServerId, status } = lastMessage;

    const entity = 'config';

    if (isDomainDeploymentInfoSuccess && domainId) {
      queryClient.setQueryData<
        BaseQueryParams<typeof useGetConfigDomainsQuery>['data']
      >([DomainsQueryKeys.GetConfig, domainId], (data) => {
        return {
          ...data,
          configInfo: {
            ...data?.configInfo,
            proxyServerAddress:
                proxyServerId === data?.configInfo?.proxyServer?.id && addressId === data?.configInfo?.proxyServerAddress?.id
                  ? {
                    ...data?.configInfo?.proxyServerAddress,
                    healthStatus: data?.configInfo?.proxyServerAddress.healthStatus,
                    configurationStatus: entity === 'config'
                      ? status
                      : data?.configInfo?.proxyServerAddress.configurationStatus
                  }
                  : data?.configInfo?.proxyServerAddress
          }
        } as BaseQueryParams<typeof useGetConfigDomainsQuery>['data'];
      });
    }
  });

  useAppSocket<Omit<Data, 'entity'>>(ProxyServerAddressesGatewayKeys.UpdateHealthStatus, ({ lastMessage }) => {
    const { addressId, proxyServerId, status } = lastMessage;

    const entity = 'health';

    if (isDomainDeploymentInfoSuccess && domainId) {
      queryClient.setQueryData<
        BaseQueryParams<typeof useGetConfigDomainsQuery>['data']
      >([DomainsQueryKeys.GetConfig, domainId], (data) => {
        return {
          ...data,
          configInfo: {
            ...data?.configInfo,
            proxyServerAddress:
              proxyServerId === data?.configInfo?.proxyServer?.id && addressId === data?.configInfo?.proxyServerAddress?.id
                ? {
                  ...data?.configInfo?.proxyServerAddress,
                  healthStatus: entity === 'health'
                    ? status
                    : data?.configInfo?.proxyServerAddress.healthStatus,
                  configurationStatus: data?.configInfo?.proxyServerAddress.configurationStatus
                }
                : data?.configInfo?.proxyServerAddress
          }
        } as BaseQueryParams<typeof useGetConfigDomainsQuery>['data'];
      });
    }
  });
};
